import { tokenize } from "./tokenizer";
import { extractModifier, ModifierType } from "./modifier";
import { isArbitrary, extractArbitrary } from "./arbitrary";

export interface ParsedClass {
  raw: string;
  modifier: ModifierType;
  utility: string;
  value: string;
  isArbitrary: boolean;
}

/**
 * Multi-token utilities that consume more than one token for their key.
 * Order matters — longer matches first.
 */
const MULTI_TOKEN_UTILITIES = [
  "grid-cols", "grid-rows", "grid-flow", "auto-cols", "auto-rows",
  "col-span", "col-start", "col-end", "row-span", "row-start", "row-end",
  "place-items", "place-content", "place-self",
  "border-t", "border-b", "border-l", "border-r", "border-x", "border-y",
  "rounded-tl", "rounded-tr", "rounded-bl", "rounded-br",
  "rounded-t", "rounded-b", "rounded-l", "rounded-r",
  "inset-x", "inset-y",
  "gap-x", "gap-y",
  "space-x", "space-y",
  "max-w", "min-w", "max-h", "min-h",
  "overflow-x", "overflow-y",
  "translate-x", "translate-y",
  "scale-x", "scale-y",
  "skew-x", "skew-y",
  "flex-row", "flex-col", "flex-wrap",
  "font-sans", "font-serif", "font-mono",
  "bg-cover", "bg-contain", "bg-auto", "bg-center", "bg-top", "bg-bottom",
  "bg-left", "bg-right", "bg-no-repeat", "bg-repeat", "bg-fixed",
  "bg-local", "bg-scroll", "bg-left-top", "bg-right-top",
  "bg-left-bottom", "bg-right-bottom", "bg-repeat-x", "bg-repeat-y",
  "text-left", "text-center", "text-right", "text-justify",
  "text-ellipsis", "text-clip",
  "object-cover", "object-contain", "object-fill", "object-none",
  "object-scale-down", "object-center", "object-top", "object-bottom",
  "object-left", "object-right", "object-left-top", "object-right-top",
  "object-left-bottom", "object-right-bottom",
  "outline-none", "outline-offset",
  "aspect-square", "aspect-video", "aspect-auto",
  "line-clamp",
  "flex-1", "flex-auto", "flex-initial", "flex-none",
  "grow-0", "shrink-0",
  "cursor-auto", "cursor-default", "cursor-pointer", "cursor-wait",
  "cursor-text", "cursor-move", "cursor-not-allowed", "cursor-grab",
  "cursor-grabbing", "cursor-crosshair", "cursor-zoom-in", "cursor-zoom-out",
  "cursor-none",
  "pointer-events",
  "select-none", "select-text", "select-all", "select-auto",
  "box-border", "box-content",
  "float-left", "float-right", "float-none",
  "clear-left", "clear-right", "clear-both", "clear-none",
  "whitespace-normal", "whitespace-nowrap", "whitespace-pre",
  "break-normal", "break-words", "break-all",
  "inline-block", "inline-flex", "inline-grid",
  "table-cell", "table-row",
  "flow-root",
  "list-none", "list-disc", "list-decimal",
  "resize-x", "resize-y", "resize-none",
  "origin-center", "origin-top", "origin-right", "origin-bottom", "origin-left",
  "origin-top-right", "origin-top-left", "origin-bottom-right", "origin-bottom-left",
  "ease-in-out", "ease-in", "ease-out",
  "transition-all", "transition-colors", "transition-opacity",
  "transition-shadow", "transition-transform", "transition-none",
  "appearance-none", "appearance-auto",
  "border-solid", "border-dashed", "border-dotted", "border-double",
  "border-hidden", "border-none",
  "overflow-hidden", "overflow-auto", "overflow-scroll", "overflow-visible",
  "overflow-clip",
  "visible", "invisible",
];

export function parseClass(cls: string): ParsedClass {
  const { modifier, rest } = extractModifier(cls);
  const tokens = tokenize(rest);

  // Try to match multi-token utility keys (longest first)
  for (const multi of MULTI_TOKEN_UTILITIES) {
    const multiTokens = multi.split("-");
    const joined = tokens.slice(0, multiTokens.length).join("-");
    if (joined === multi) {
      const valueTokens = tokens.slice(multiTokens.length);
      const rawValue = valueTokens.join("-");
      const arbitrary = rawValue && isArbitrary(rawValue);
      return {
        raw: cls,
        modifier,
        utility: multi,
        value: arbitrary ? extractArbitrary(rawValue) : rawValue,
        isArbitrary: !!arbitrary,
      };
    }
  }

  // Single-token utility
  const utility = tokens[0] ?? "";
  const rawValue = tokens.slice(1).join("-");
  const arbitrary = rawValue && isArbitrary(rawValue);

  return {
    raw: cls,
    modifier,
    utility,
    value: arbitrary ? extractArbitrary(rawValue) : rawValue,
    isArbitrary: !!arbitrary,
  };
}
